import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { baseTheme } from "@avenue-8/sidekick-ui";
import { RotatingSignalCard } from "./RotatingSignalCard";

const CENTER = 200;
const OUTER_RADIUS = 170;
const POINTER_ANGLE_DEG = 45; // ~1:30

const blink = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

const GRID_CELL_PX = 32;

const Frame = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
  aspect-ratio: 1;
  border-radius: 1.5rem;
  overflow: hidden;
  background-color: ${(p) => p.theme.colors.background};
  background-image:
    linear-gradient(to right, ${baseTheme.colors.blue2}1f 1px, transparent 1px),
    linear-gradient(to bottom, ${baseTheme.colors.blue2}1f 1px, transparent 1px),
    radial-gradient(
      circle at 85% 12%,
      ${baseTheme.colors.green1} 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 30% 20%,
      ${baseTheme.colors.blue1} 0%,
      transparent 70%
    );
  background-size:
    ${GRID_CELL_PX}px ${GRID_CELL_PX}px,
    ${GRID_CELL_PX}px ${GRID_CELL_PX}px,
    auto,
    auto;
`;

const SweepGlow = styled.div`
  position: absolute;
  inset: ${(50 - (OUTER_RADIUS / 400) * 100).toFixed(2)}%;
  border-radius: 50%;
  background: conic-gradient(
    from ${POINTER_ANGLE_DEG}deg,
    ${baseTheme.colors.green2}55 0%,
    transparent 90deg,
    transparent 360deg
  );
`;

const RadarSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const BaseRing = styled.circle`
  fill: none;
  stroke: ${baseTheme.colors.blue2};
  stroke-opacity: 0.3;
  stroke-width: 2;
`;

const Blip = styled.span<{
  top: string;
  left: string;
  color: string;
  visible: boolean;
}>`
  position: absolute;
  top: ${(p) => p.top};
  left: ${(p) => p.left};
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  background-color: ${(p) => p.color};
  border: 1.5px solid ${(p) => p.theme.colors.background};
  opacity: ${(p) => (p.visible ? 1 : 0)};
  transition: opacity 0.4s ease, top 0.4s ease, left 0.4s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: ${(p) => p.color};
    animation: ${blink} 2.4s cubic-bezier(0, 0, 0.2, 1) infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      content: none;
    }
  }
`;

const RINGS = [170, 115, 60];

const BLIP_COLORS = [
  baseTheme.colors.coral2,
  baseTheme.colors.green2,
  "#F5C451",
];

// Three hand-picked slots per dot, spaced ~40° apart around the rings and
// each dot kept on its own radius band, so no combination of slots ever
// lands two dots close together.
const DOT_POSITIONS = [
  [
    { top: "33.6%", left: "54.4%" },
    { top: "62%", left: "62%" },
    { top: "54.4%", left: "33.6%" },
  ],
  [
    { top: "36.6%", left: "69.2%" },
    { top: "73.3%", left: "52%" },
    { top: "40.1%", left: "28.8%" },
  ],
  [
    { top: "52.6%", left: "79.6%" },
    { top: "74.4%", left: "32.9%" },
    { top: "23%", left: "37.4%" },
  ],
];

const CyclingBlip = ({
  color,
  positions,
  initialDelay,
}: {
  color: string;
  positions: { top: string; left: string }[];
  initialDelay: number;
}) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const hide = () => {
      setVisible(false);
      timeoutId = setTimeout(advance, 400);
    };

    const advance = () => {
      setIndex((prev) => (prev + 1) % positions.length);
      setVisible(true);
      timeoutId = setTimeout(hide, 3000 + Math.random() * 1000);
    };

    timeoutId = setTimeout(advance, initialDelay);
    return () => clearTimeout(timeoutId);
  }, [initialDelay, positions.length]);

  const position = positions[index];
  return (
    <Blip
      top={position.top}
      left={position.left}
      color={color}
      visible={visible}
    />
  );
};

export const RadarGraphic = () => (
  <Frame>
    <SweepGlow />
    <RadarSvg viewBox="0 0 400 400">
      {RINGS.map((r) => (
        <BaseRing key={r} cx={CENTER} cy={CENTER} r={r} />
      ))}
      <line
        x1={CENTER}
        y1={CENTER}
        x2={
          CENTER +
          OUTER_RADIUS * Math.sin((POINTER_ANGLE_DEG * Math.PI) / 180)
        }
        y2={
          CENTER -
          OUTER_RADIUS * Math.cos((POINTER_ANGLE_DEG * Math.PI) / 180)
        }
        stroke={baseTheme.colors.green3}
        strokeWidth={2}
      />
      <circle cx={CENTER} cy={CENTER} r={4} fill={baseTheme.colors.green3} />
    </RadarSvg>
    {BLIP_COLORS.map((color, index) => (
      <CyclingBlip
        key={color}
        color={color}
        positions={DOT_POSITIONS[index]}
        initialDelay={index * 700}
      />
    ))}
    <RotatingSignalCard />
  </Frame>
);
